import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Statistic, Row, Col, Modal, App, Alert } from 'antd';
import { ReloadOutlined, UserOutlined, TeamOutlined, SolutionOutlined, EditOutlined, MailOutlined } from '@ant-design/icons';
import { PageHeader } from '@municipality/ui';
import { useAllUsersAndInvitations } from '../hooks/useAllUsersAndInvitations';
import UserTable from '../components/UserTable';
import SuperAdminUserForm from '../components/SuperAdminUserForm';
import { AllUser } from '../hooks/useAllUsersAndInvitations';
import { SuperAdminInvitationService } from '../services/invitations';
import { SuperAdminUserService } from '../services/users';
import { useAuth } from '../hooks/useAuth';
import { SuperAdminRole } from '@municipality/types';

const Users: React.FC = () => {
  const { t } = useTranslation('users');
  const { message } = App.useApp();
  const { user: currentUser } = useAuth();
  const { users, invitations, allItems, loading, error } = useAllUsersAndInvitations();
  const [selectedUser, setSelectedUser] = useState<AllUser | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleEdit = (user: AllUser) => {
    setSelectedUser(user);
    setModalVisible(true);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setSelectedUser(null);
  };

  interface UserFormValues {
    email: string;
    name: string;
    surname: string;
    phoneNumber: string;
    superAdminRole: SuperAdminRole;
    status?: 'active' | 'inactive' | 'pending';
  }

  const handleSubmit = async (values: UserFormValues) => {
    if (!currentUser?.uid) {
      message.error(t('notAuthenticated', 'Not authenticated'));
      return;
    }

    setSubmitting(true);
    try {
      if (selectedUser) {
        // Update existing user
        await SuperAdminUserService.updateUser(selectedUser.id, {
          displayName: `${values.name} ${values.surname}`,
          name: values.name,
          surname: values.surname,
          phoneNumber: values.phoneNumber,
          superAdminRole: values.superAdminRole,
          status: values.status,
        });
        message.success(t('userUpdated', 'User updated successfully'));
      } else {
        // Send invitation
        await SuperAdminInvitationService.inviteUser(
          {
            email: values.email,
            name: values.name,
            surname: values.surname,
            phoneNumber: values.phoneNumber,
            superAdminRole: values.superAdminRole,
            invitedBy: currentUser.uid,
          },
          currentUser.uid
        );
        message.success(t('invitationSent', 'Invitation sent successfully'));
      }
      
      setModalVisible(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error(
        selectedUser 
          ? t('updateError', 'Failed to update user') 
          : t('invitationError', 'Failed to send invitation')
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate statistics
  const totalUsers = users.length;
  const administrators = users.filter(user => user.superAdminRole === 'administrator' || user.superAdminRole === 'super_admin').length;
  const regionManagers = users.filter(user => user.superAdminRole === 'region_manager').length;
  const contentManagers = users.filter(user => user.superAdminRole === 'content_manager').length;
  const pendingInvitations = invitations.length;

  if (error) {
    return (
      <div>
        <PageHeader
          title={t('title', 'Users')}
          subtitle={t('subtitle', 'Manage super admin users')}
        />
        <Alert
          message={t('errorLoadingUsers', 'Error loading users')}
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" icon={<ReloadOutlined />}>
              {t('retry', 'Retry')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t('title', 'Users')}
        subtitle={t('subtitle', 'Manage super admin users')}
      />
      
      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={{ flex: '20%' }} xl={{ flex: '20%' }}>
          <Card style={{ background: '#595959', borderRadius: 8, border: 'none' }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>{t('totalUsers', 'Total users')}</span>}
              value={totalUsers}
              prefix={<TeamOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={{ flex: '20%' }} xl={{ flex: '20%' }}>
          <Card style={{ background: '#cf1322', borderRadius: 8, border: 'none' }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>{t('administrators', 'Administrators')}</span>}
              value={administrators}
              prefix={<UserOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={{ flex: '20%' }} xl={{ flex: '20%' }}>
          <Card style={{ background: '#1890ff', borderRadius: 8, border: 'none' }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>{t('regionManagers', 'Region managers')}</span>}
              value={regionManagers}
              prefix={<SolutionOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={{ flex: '20%' }} xl={{ flex: '20%' }}>
          <Card style={{ background: '#13c2c2', borderRadius: 8, border: 'none' }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>{t('contentManagers', 'Content managers')}</span>}
              value={contentManagers}
              prefix={<EditOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={{ flex: '20%' }} xl={{ flex: '20%' }}>
          <Card style={{ background: '#fa8c16', borderRadius: 8, border: 'none' }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>{t('pendingInvitations', 'Pending invitations')}</span>}
              value={pendingInvitations}
              prefix={<MailOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Users Table */}
      <UserTable
        users={allItems}
        loading={loading}
        onEdit={handleEdit}
        onAdd={() => {
          setSelectedUser(null);
          setModalVisible(true);
        }}
      />

      {/* User Modal with SuperAdminUserForm */}
      <Modal
        title={null}
        open={modalVisible}
        onCancel={handleModalCancel}
        footer={null}
        width={700}
        destroyOnHidden
      >
        <SuperAdminUserForm
          user={selectedUser ? {
            id: selectedUser.id,
            email: selectedUser.email,
            name: ('name' in selectedUser) ? selectedUser.name as string : undefined,
            surname: ('surname' in selectedUser) ? selectedUser.surname as string : undefined,
            phoneNumber: ('phoneNumber' in selectedUser) ? selectedUser.phoneNumber as string : undefined,
            superAdminRole: ('superAdminRole' in selectedUser) ? selectedUser.superAdminRole as SuperAdminRole : SuperAdminRole.CONTENT_MANAGER,
            status: selectedUser.status as 'active' | 'inactive' | 'pending',
          } : null}
          onSubmit={handleSubmit}
          loading={submitting}
          onCancel={handleModalCancel}
        />
      </Modal>
    </div>
  );
};

export default Users; 